import 'css/src/data/transcript/nodes/BaseOverlayElementNode.module.scss';

import classNames from 'classnames';
import type { LexicalNode, NodeKey, SerializedElementNode, Spread } from 'lexical';
import { ElementNode } from 'lexical';

import { $createSceneNumberNode } from './SceneNumberNode';

const BaseOverlayElementNodeLexicalType = 'base-insert-node'; // DO NOT CHANGE THIS TYPE OR ELSE TRANSCRIPT WILL NOT RENDER

export type SerializedBaseOverlayElementNode =
    | Spread<
          {
              sceneNumber?: number;
              className: string;
              type: typeof BaseOverlayElementNodeLexicalType;
              version: 1;
          },
          SerializedElementNode
      >
    | Spread<
          {
              // in version 2, we drop the scene number because that is now a SceneNumberNode
              className: string;
              type: typeof BaseOverlayElementNodeLexicalType;
              version: 2;
          },
          SerializedElementNode
      >;

/**
 * BaseOverlayElementNode is a component of the more complete 'OverlayElementNode'
 * It's an overlay with word nodes as children but without the drag handle.
 * It should only be instantiated as part of an 'OverlayElementNode' via `$createOverlayElementNode`
 *
 * @see OverlayElementNode for tree structure in lexical
 */
export class BaseOverlayElementNode extends ElementNode {
    __className: string;

    constructor({ className, key }: { className?: string; key?: NodeKey } = {}) {
        super(key);
        this.__className = className || '';
    }

    static override getType(): string {
        return BaseOverlayElementNodeLexicalType;
    }

    static override clone(node: BaseOverlayElementNode): BaseOverlayElementNode {
        return new BaseOverlayElementNode({
            key: node.__key,
            className: node.__className,
        });
    }

    override createDOM(): HTMLElement {
        // Create base overlay with icon
        const overlayWithSceneNumber = document.createElement('span');
        return this.__update(overlayWithSceneNumber);
    }

    override updateDOM(prevNode: BaseOverlayElementNode, dom: HTMLSpanElement): boolean {
        this.__update(dom);
        return false;
    }

    __update(domElement: HTMLSpanElement) {
        domElement.className = classNames('base-overlay-node', this.__className);
        return domElement;
    }

    static override importJSON(
        serializedNode: SerializedBaseOverlayElementNode,
    ): BaseOverlayElementNode {
        if (serializedNode.version === 1) {
            return $createBaseOverlayElementNode(
                serializedNode.sceneNumber || 1, // the scene number doesn't reallllly matter here, the LuminarySyncPlugin will correct it if it's wrong.
                {
                    className: serializedNode.className,
                },
            );
        } else if (serializedNode.version === 2) {
            return new BaseOverlayElementNode({
                className: serializedNode.className,
            });
        } else {
            throw new Error(
                `Unsupported BaseOverlayElementNode version: ${JSON.stringify(serializedNode)}`,
            );
        }
    }

    override exportJSON(): SerializedBaseOverlayElementNode {
        return {
            ...super.exportJSON(),
            className: this.__className,
            type: BaseOverlayElementNodeLexicalType,
            version: 2,
        };
    }

    override isInline(): boolean {
        // when lexical talks about "inline", it means that the node can be split into
        // multiple nodes, with TextNodes as siblings. This happens during paste operations,
        // for example.
        // However, only nodes that are inline can be empty. So if you delete all the content
        // inside of the node, lexical will add a `<br/>` tag to make sure that the node has some content.
        return false;
    }

    set className(className: string) {
        if (this.className !== className) {
            this.getWritable().__className = className;
        }
    }

    get className(): string {
        const self = this.getLatest();
        return self.__className;
    }
}

export function $isBaseOverlayElementNode(
    node: LexicalNode | null | undefined,
): node is BaseOverlayElementNode {
    return node instanceof BaseOverlayElementNode;
}

export function $createBaseOverlayElementNode(
    sceneNumber: string | number,
    { className, key }: { className?: string; key?: NodeKey },
): BaseOverlayElementNode {
    const sceneNumberNode = $createSceneNumberNode(sceneNumber.toString());
    const baseOverlayElementNode = new BaseOverlayElementNode({
        className,
        key,
    });
    baseOverlayElementNode.append(sceneNumberNode);
    return baseOverlayElementNode;
}
